import type { CellClassParams, CellStyle } from "ag-grid-community"

import { collectRoles } from "./ast"
import { type Formula, type InspectedCell, ROLE_COLORS } from "./types"

// Keys we touch in the inspection style. Always present on every return path so
// `refreshCells({ force: true })` clears inline styles cleanly on deselect —
// omitting a key would leave the previous value stuck on the cell.
type InspectionStyle = Record<
  | "text-decoration"
  | "text-decoration-style"
  | "text-underline-offset"
  | "cursor"
  | "outline"
  | "outline-offset"
  | "background-color"
  | "color",
  string
>

const EMPTY_INSPECTION_STYLE: InspectionStyle = {
  "text-decoration": "",
  "text-decoration-style": "",
  "text-underline-offset": "",
  cursor: "",
  outline: "",
  "outline-offset": "",
  "background-color": "",
  color: "",
}

/**
 * Computes the inspection-mode styling for a single cell.
 * Returns a style object with all `INSPECTION_KEYS` set — empty strings clear
 * inline styles that would otherwise stick after `refreshCells`.
 *
 *   - Inspectable cells always show a dotted underline + pointer cursor.
 *   - The inspected cell itself gets a focus ring.
 *   - Cells in the inspected row that are operands of the inspected formula
 *     get a tag-colored background (addend / detractor / divisor).
 */
export function getInspectionCellStyle<T>(
  params: CellClassParams<T>,
  colId: string,
  formulasByColId: Map<string, Formula>,
): InspectionStyle {
  const style: InspectionStyle = { ...EMPTY_INSPECTION_STYLE }
  const isInspectable = formulasByColId.has(colId)
  const isGroup = params.node?.group ?? false

  if (isInspectable && !isGroup) {
    style["text-decoration"] = "underline"
    style["text-decoration-style"] = "dotted"
    style["text-underline-offset"] = "3px"
    style.cursor = "pointer"
  }

  if (isGroup || !params.data) return style

  const ctx = params.context as { inspectedCell?: InspectedCell | null } | undefined
  const ic = ctx?.inspectedCell
  if (!ic) return style

  const rowId = (params.node as { id?: string })?.id ?? null
  if (rowId !== ic.rowId) return style

  // The inspected cell itself: focus ring.
  if (ic.colId === colId) {
    style.outline = "2px solid var(--color-fg-interactive)"
    style["outline-offset"] = "-2px"
    return style
  }

  // Same row, related to the inspected column: tag-colored bg from the shared
  // palette. `result` role is never produced by `collectRoles`, so the lookup
  // only ever matches addend/detractor/divisor here.
  const formula = formulasByColId.get(ic.colId)
  if (!formula) return style
  const role = collectRoles(formula).get(colId)
  if (role && role !== "result") {
    const { bg, fg } = ROLE_COLORS[role]
    style["background-color"] = bg
    style.color = fg
  }
  return style
}

/**
 * Composes inspection styling on top of a colDef's existing `cellStyle`.
 * Supports object, function, and undefined forms.
 */
export function composeCellStyle<T>(
  original: CellStyle | ((params: CellClassParams<T>) => CellStyle | null | undefined) | undefined,
  inspection: InspectionStyle,
  params: CellClassParams<T>,
): CellStyle {
  const base = typeof original === "function" ? original(params) : original
  return { ...(base ?? {}), ...inspection }
}
